import type { AiReplyEnvelope } from "./message-mapping";

// The boundary between the staff workspace and Stream (CHT-732; docs/engineering/staff-workspace.md, ADR 0009). Everything in
// `src/conversations` speaks THESE types. Exactly one file, `stream-adapter.ts`, imports `stream-chat`; a test swaps it for
// `test/fake-messaging.ts`. Nothing here names a Stream type, a Stream user id format other than the plain strings the session
// route gave us, or an SDK event.

export type MessageAuthorKind =
  | "CUSTOMER"
  | "LAFAYETTE_AI"
  | "CONCIERGE"
  | "SYSTEM"
  | "COPILOT"
  | "UNKNOWN";

export type MessageStatus = "sent" | "sending" | "failed";

export type ThreadAttachment = Readonly<{
  kind: "IMAGE" | "FILE";
  name: string;
  /** The URL Stream serves (a Stream CDN host), or `null` when there is none yet (an upload in progress) or the host is not trusted. */
  url: string | null;
  /**
   * An https address on a host that is NOT a Stream CDN host. A customer client can post any address, so the console never loads it
   * by itself (it would tell that host the staff member's IP address and the time of reading). It shows the host and lets staff open it.
   */
  external: Readonly<{ host: string; url: string }> | null;
  mimeType: string | null;
  sizeBytes: number | null;
}>;

/** One message as the workspace draws it. Built from a `RawMessage` by `message-mapping.ts`. */
export type ThreadMessage = Readonly<{
  id: string;
  authorKind: MessageAuthorKind;
  /** The display name Stream holds for the author. Never an email or a number (the server sets it so). */
  authorName: string;
  /** The message was written by this staff member. */
  own: boolean;
  text: string;
  createdAt: string;
  status: MessageStatus;
  attachments: readonly ThreadAttachment[];
  /** The parsed Lafayette envelope of an AI reply, or `null`. A system notice has its own field. */
  ai: AiReplyEnvelope | null;
  /** The code of a customer-safe system notice, or `null`. */
  systemNotice:
    | "CONNECTING_TO_CONCIERGE"
    | "CONCIERGE_JOINED"
    | "CONCIERGE_CHANGED"
    | "AI_RESUMED"
    | null;
  deleted: boolean;
}>;

/**
 * The state of the connection to Stream as the staff member needs to know it.
 * `EXPIRED`: the session route refused a new token (401 or 403): the staff member must sign in again, or lost access.
 */
export type ConnectionState = "CONNECTING" | "ONLINE" | "OFFLINE" | "EXPIRED";

export type ThreadSnapshot = Readonly<{
  messages: readonly ThreadMessage[];
  connection: ConnectionState;
  hasMoreOlder: boolean;
  loadingOlder: boolean;
  /** Messages from others that arrived after the last read mark. */
  unreadCount: number;
}>;

/** Why a thread could not be opened. The workspace shows a different message for each. */
export type ThreadFailureKind =
  /** The staff user is not a member of the channel (Stream refused). Joining through the API may fix it. */
  | "NOT_MEMBER"
  /** The channel does not exist in Stream. */
  | "NOT_FOUND"
  /** The session route refused a token: sign in again, or the access ended. */
  | "SESSION"
  /** Stream is unreachable or answered with an error. Retrying may work. */
  | "UNAVAILABLE";

export class ThreadClientError extends Error {
  readonly kind: ThreadFailureKind;

  constructor(kind: ThreadFailureKind, options?: { cause?: unknown }) {
    super(`the conversation thread could not be opened (${kind})`, options);
    this.name = "ThreadClientError";
    this.kind = kind;
  }
}

export type OutgoingMessage = Readonly<{
  /** A lowercase uuid v4 (contract section 3.5). The workspace makes it; the adapter never invents another shape. */
  id: string;
  text: string;
  files: readonly File[];
}>;

/**
 * `NOT_OWNER`: the workspace refused BEFORE anything reached Stream, because the server says this staff member no longer owns the
 * open handoff (or the session is not the one that is connected). `UNVERIFIED`: the same refusal, because the check itself could not
 * be made (the context read failed): the workspace does not guess. `REJECTED`: Stream or the server refused the message.
 */
export type SendFailure = "OFFLINE" | "REJECTED" | "UNKNOWN" | "NOT_OWNER" | "UNVERIFIED";
export type SendResult = Readonly<{ ok: true }> | Readonly<{ ok: false; reason: SendFailure }>;

/** One open customer channel. The ONLY object in the workspace that can write to the customer thread. */
export interface ThreadClient {
  getSnapshot(): ThreadSnapshot;
  subscribe(listener: () => void): () => void;
  /** Watches the channel. Throws `ThreadClientError`. Safe to call once. */
  start(): Promise<void>;
  stop(): Promise<void>;
  /** Loads one page of older messages. Rejects with `ThreadClientError` when the request fails (the list is unchanged then). */
  loadOlder(): Promise<void>;
  /** Sends a message to the CUSTOMER channel as the signed-in staff member. Never throws: the result says what happened. */
  send(message: OutgoingMessage): Promise<SendResult>;
  /** Sends a failed message again with the same id, with the same text and files. */
  retry(messageId: string): Promise<SendResult>;
  /** Drops a failed message that was never delivered, with its files. Does nothing to a message that was sent. */
  discard(messageId: string): void;
  /** Calls the listener when the members of the channel change (Stream member events). Returns what undoes it. */
  subscribeMembership(listener: () => void): () => void;
  /** Marks the channel read for this staff member. Failure is silent. */
  markRead(): Promise<void>;
}

/** The session the workspace gives to the connection. It comes from `POST /staff/messaging/session`. */
export type MessagingSessionInput = Readonly<{
  apiKey: string;
  /** The Stream user id of this staff member (`<ns>-staff-<uuid>`). */
  userId: string;
  token: string;
}>;

/**
 * One Stream connection for the signed-in staff member, for the life of the workspace. It joins NO channel by itself: a thread is
 * watched only through `openThread`, and membership comes from the server (computed set or the join route).
 */
export interface MessagingConnection {
  getState(): ConnectionState;
  subscribe(listener: () => void): () => void;
  openThread(channel: Readonly<{ type: "messaging"; id: string }>): ThreadClient;
  /** Unread counts of the given channels. A channel the user cannot read counts 0. Never throws. Overlay only: it never changes order. */
  unreadCounts(channelIds: readonly string[]): Promise<Readonly<Record<string, number>>>;
  disconnect(): Promise<void>;
}

export type ConnectMessaging = (
  input: Readonly<{
    session: MessagingSessionInput;
    /** Fetches a new token from the session route. Throws when the route refuses (the connection then reports `EXPIRED`). */
    refreshToken: () => Promise<string>;
  }>,
  // A promise, so the real connector can load the Stream SDK on first use and keep it out of the first page load of the console.
) => MessagingConnection | Promise<MessagingConnection>;
